/**
 * Wypełnianie wzoru PDF operatora: wspólny rdzeń dla wszystkich OSD.
 *
 * Polecenia (każde pole opcjonalne):
 * - `tekst`: { nazwaPola: wartość } dla pól tekstowych formularza (Enea, Energa, Tauron);
 * - `wybor`: { nazwaGrupy: indeks albo nazwa opcji } dla grup radio i list;
 * - `zaznacz`: [nazwaPola] dla pól wyboru; `odznacz`: [nazwaPola] zdejmuje zaznaczenie (poprawki z podglądu);
 * - `pokaz`: [nazwaPola] odkrywa ukryte pola wzoru razem z polami podrzędnymi („Text14” to też „Text14.0.1”).
 *   Energa skreśla tak słowa „wniosek” / „zgłoszenie” i „nowy” / „istniejący”: kreska jest ukrytym polem,
 *   które wzór pokazuje kliknięciem w słowo; po odkryciu klient nadal może ją przełączyć w czytniku PDF;
 * - `nadruk`: [{ strona, x0, y0, x1, y1, tekst, rozmiar, wyrownanie, komorki, krok }] dla PŁASKICH wzorów (PGE):
 *   tekst w prostokącie; współrzędne w punktach od LEWEGO GÓRNEGO rogu strony, jak `page.rect` i słowa
 *   w pymupdf, więc da się je przepisać prosto z pomiaru. `komorki` (liczba kratek) albo `krok` (szerokość
 *   kratki od x0) rozkłada znaki po jednym na kratkę, jak w polach na PESEL czy kod pocztowy;
 * - `krzyzyki`: [{ strona, x0, y0, x1, y1 }] krzyżyk w kwadracie wyboru płaskiego wzoru.
 * Nadruk i krzyżyki stają się polami formularza (tekstowym i wyboru) w zmierzonych miejscach, a nie rysunkiem:
 * dzięki temu wartości da się poprawić w podglądzie i w czytniku PDF. Nazwy: `pole` z polecenia albo
 * `nadruk_<strona>_<nr>` / `kratka_<strona>_<nr>`.
 * Strony liczone od 0.
 *
 * `wypelnijBajty` jest czysta (bajty na wejściu i wyjściu), więc testy wypełniają prawdziwy wzór w node.
 * Pola zostają edytowalne: klient może poprawić wniosek w czytniku PDF przed podpisem.
 */

/**
 * Mniejsza czcionka, gdy tekst nie mieści się w szerokości pola (np. „moduł PV JAM60D41-505/LB” w wąskiej
 * kolumnie tabeli Enei): od 10 pt w dół do 6 pt. Bez tego tekst wychodził na sąsiednią kolumnę albo był ucinany.
 * Pola bez wpisu /DA (załącznik C Enei) dostają domyślny, bo bez niego pdf-lib nie zmieni rozmiaru.
 * Rozmiar to kosmetyka: błąd tutaj nie może zablokować pliku.
 */
const dopasujCzcionke = (p, tekst, czcionka) => {
  try {
    const szerokosc = p.acroField.getWidgets()[0]?.getRectangle()?.width
    if (!szerokosc) return
    let rozmiar = 10
    while (rozmiar > 6 && czcionka.widthOfTextAtSize(tekst, rozmiar) > szerokosc - 4) rozmiar -= 0.5
    if (!p.acroField.getDefaultAppearance()) p.acroField.setDefaultAppearance('/Helv 0 Tf 0 g')
    p.setFontSize(rozmiar)
  } catch { /* zostaje rozmiar ze wzoru */ }
}

/** Prostokąt z układu „od góry” (pymupdf) na układ PDF (od dołu), z uwzględnieniem CropBox. */
const prostokatNaStronie = (strona, { x0, y0, x1, y1 }) => {
  const ramka = strona.getCropBox()
  return { x: ramka.x + x0, y: ramka.y + ramka.height - y1, szerokosc: x1 - x0, wysokosc: y1 - y0 }
}

/** Pole tekstowe w prostokącie płaskiego wzoru: bez ramki i tła, czcionka zmniejszana, gdy tekst się nie mieści. */
const poleNadruku = (form, strona, n, nazwa, czcionka) => {
  const tekst = String(n.tekst ?? '').trim()
  const r = prostokatNaStronie(strona, n)
  const tf = form.createTextField(nazwa)
  if (n.komorki) { tf.setMaxLength(n.komorki); tf.enableCombing() }
  if (tekst) tf.setText(n.komorki ? tekst.slice(0, n.komorki) : tekst)
  tf.addToPage(strona, {
    x: r.x, y: r.y, width: r.szerokosc, height: r.wysokosc, font: czcionka, backgroundColor: undefined, borderColor: undefined, borderWidth: 0,
  })
  let rozmiar = n.rozmiar || Math.min(9, r.wysokosc * 0.75)
  if (!n.komorki) while (rozmiar > 5 && czcionka.widthOfTextAtSize(tekst, rozmiar) > r.szerokosc - 4) rozmiar -= 0.25
  tf.setFontSize(rozmiar)
  if (n.wyrownanie === 'srodek') tf.setAlignment(1) // TextAlignment.Center
}

/** Pole wyboru w kwadracie płaskiego wzoru, zaznaczone. */
const kratka = (form, strona, k, nazwa) => {
  const r = prostokatNaStronie(strona, k)
  const cb = form.createCheckBox(nazwa)
  cb.addToPage(strona, { x: r.x, y: r.y, width: r.szerokosc, height: r.wysokosc, backgroundColor: undefined, borderColor: undefined, borderWidth: 0 })
  cb.check()
}

/**
 * Wypełnia wzór i zwraca bajty. `biblioteki` = { PDFDocument, rgb, fontkit } (w przeglądarce ładowane
 * dopiero przy generowaniu, w testach importowane wprost). Czcionka z polskimi znakami jest osadzana,
 * bo standardowa Helvetica nie zapisze „ł” ani „ę”.
 */
export const wypelnijBajty = async (pdfBajty, polecenia, czcionkaBajty, { PDFDocument, fontkit }) => {
  const doc = await PDFDocument.load(pdfBajty)
  doc.registerFontkit(fontkit)
  const czcionka = await doc.embedFont(czcionkaBajty, { subset: false })
  const form = doc.getForm()
  const pole = (nazwa) => { try { return form.getField(nazwa) } catch { return null } }
  let zmienioneFormularz = false

  for (const [nazwa, wartosc] of Object.entries(polecenia.tekst || {})) {
    const p = pole(nazwa)
    if (p && typeof p.setText === 'function') {
      // Pola „kratkowe” mają limit znaków; dłuższy tekst przycinamy zamiast przerywać generowanie.
      const maks = typeof p.getMaxLength === 'function' ? p.getMaxLength() : undefined
      const tekst = maks ? String(wartosc).slice(0, maks) : String(wartosc)
      p.setText(tekst)
      if (!(typeof p.isCombed === 'function' && p.isCombed())) dopasujCzcionke(p, tekst, czcionka)
      zmienioneFormularz = true
    }
  }
  for (const [nazwa, wybor] of Object.entries(polecenia.wybor || {})) {
    const p = pole(nazwa)
    if (p && typeof p.getOptions === 'function') {
      const opcje = p.getOptions()
      const opcja = typeof wybor === 'number' ? opcje[wybor] : opcje.find((o) => o === wybor)
      if (opcja != null) { p.select(opcja); zmienioneFormularz = true }
    }
  }
  for (const nazwa of polecenia.zaznacz || []) {
    const p = pole(nazwa)
    if (p && typeof p.check === 'function') { p.check(); zmienioneFormularz = true }
  }
  for (const nazwa of polecenia.odznacz || []) {
    const p = pole(nazwa)
    if (p && typeof p.uncheck === 'function') { p.uncheck(); zmienioneFormularz = true }
  }
  const strony = doc.getPages()
  ;(polecenia.nadruk || []).forEach((n, i) => {
    if (strony[n.strona]) { poleNadruku(form, strony[n.strona], n, n.pole || `nadruk_${n.strona}_${i + 1}`, czcionka); zmienioneFormularz = true }
  })
  ;(polecenia.krzyzyki || []).forEach((k, i) => {
    if (strony[k.strona]) { kratka(form, strony[k.strona], k, k.pole || `kratka_${k.strona}_${i + 1}`); zmienioneFormularz = true }
  })
  for (const nazwa of polecenia.pokaz || []) {
    const pola = form.getFields().filter((p) => p.getName() === nazwa || p.getName().startsWith(`${nazwa}.`))
    for (const p of pola) {
      // Flaga adnotacji „Hidden” to bit 2; „Print” (4) zostaje, więc kreska wychodzi też na wydruku.
      for (const w of p.acroField.getWidgets()) w.setFlags(w.getFlags() & ~2)
    }
  }
  if (zmienioneFormularz) {
    try {
      form.updateFieldAppearances(czcionka)
    } catch {
      // Jedno nietypowe pole wzoru nie może zablokować całego pliku: wygląd odświeżamy pole po polu.
      for (const p of form.getFields()) {
        try { if (typeof p.updateAppearances === 'function') p.updateAppearances(czcionka) } catch { /* zostaje wygląd ze wzoru */ }
      }
    }
  }

  return doc.save()
}

/** To samo z adresów w `public/osd/`: pdf-lib i fontkit ładujemy tutaj, dopiero przy generowaniu. */
export const wypelnijPdf = async (urlPdf, polecenia, urlCzcionki) => {
  const [{ PDFDocument, rgb }, fontkitModul] = await Promise.all([import('pdf-lib'), import('@pdf-lib/fontkit')])
  const fontkit = fontkitModul.default || fontkitModul
  const [pdfBajty, czcionkaBajty] = await Promise.all([
    fetch(urlPdf).then((r) => { if (!r.ok) throw new Error(`Nie udało się pobrać wzoru (${r.status}).`); return r.arrayBuffer() }),
    fetch(urlCzcionki).then((r) => r.arrayBuffer()),
  ])
  return wypelnijBajty(pdfBajty, polecenia, czcionkaBajty, { PDFDocument, rgb, fontkit })
}
